import React, { useEffect } from 'react';
import { Layout, LayoutProps, SidebarProps, useAuthProvider } from 'react-admin';
import COCAppBar from './COCAppBar';
import COCSideBar from './COCSideBar';
import COCMenu from './menu/COCMenu';
import { useTheme } from '@mui/material/styles';
import { LLLoaderComponent } from '@/components-library';
import { useGetIdentity, useStore } from 'ra-core';
import { AppLocationContext } from '../components/AppLocation';
import { useQuery, useQueryClient } from 'react-query';
import { Dialog } from '@mui/material';
import { formSx } from '@/src/shared/styles/formSx';
import { Resources } from '@/src/shared/constants/resources';
import { UserFields, UserRoles } from '@/src/shared/constants/users';
import AdminSwitchModal from '@/src/pages/adminSwitch/AdminSwitchModal';
import { useLocation } from 'react-router-dom';
import { ClubCreateAdmin } from '@/src/pages/clubProfile/create/ClubCreateAdmin';
import { ClubCreateUser } from '@/src/pages/clubProfile/create/ClubCreateUser';
import RedirectBillingIfNoSubscription from '@/src/layout/RedirectBillingIfNoSubscription';
import { doc, getDoc } from 'firebase/firestore';
import { firebaseStorage } from '@/src/auth/FirebaseAuth';
import { useImpersonatingUid } from '@/src/shared/hooks/useImpersonatingUid';

const AppLayout = (props: LayoutProps) => {
  const theme = useTheme();
  const sx = {
    root: {
      [theme.breakpoints.down('sm')]: {
        mt: '32px',
      },
      '& .RaLayout-content': {
        zIndex: 'unset',
      },
    },
  };

  useQuery([`/auth/login`], useAuthProvider().refreshSession, {
    refetchInterval: 5 * 60 * 1000,
    refetchIntervalInBackground: true,
  });

  const { identity, isLoading: isIdentityLoading } = useGetIdentity();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isCreateClub = /.*\/club-profile\/create.*/.test(location?.pathname);

  const [isEmployee, setIsEmployee] = useStore('isEmployee', true);
  const hasClubAdminUids = identity?.[UserFields.ClubAdminUids]?.length > 0;
  const isClubAdmin = identity?.[UserFields.Roles]?.includes(UserRoles.ClubAdmin);
  const impersonatingId = useImpersonatingUid([identity?.[UserFields.Uid]]);
  const {
    data: club,
    isLoading: isClubLoading,
    isFetched: isClubFetched,
  } = useQuery(
    [Resources.Clubs, identity?.[UserFields.Uid], 'checkExists'],
    async () => {
      if (!identity?.[UserFields.Uid]) return null;
      const clubDocRef = doc(firebaseStorage, Resources.Clubs, identity[UserFields.Uid]);
      const clubDoc = await getDoc(clubDocRef);
      if (!clubDoc.exists()) {
        return null;
      }
      return { ...clubDoc.data(), id: clubDoc.id };
    },
    {
      enabled: !!identity?.[UserFields.Uid] && !isIdentityLoading,
    }
  );

  useEffect(() => {
    if (isIdentityLoading || !isClubFetched) return;
    setIsEmployee(
      !!impersonatingId ||                          // switched into another club → limited view
      (hasClubAdminUids && !isClubAdmin && !club),  // pure employee without own club yet
    );
  }, [!!impersonatingId, hasClubAdminUids, isClubAdmin, !!club, isIdentityLoading, isClubFetched]);

  // Wait for club query to resolve before showing — prevents flash when club exists but query is still loading
  const showAdminSwitch =
    !isCreateClub && hasClubAdminUids && !isClubAdmin && !isIdentityLoading && !club && isClubFetched && !isClubLoading;

  const showClubCreateModal =
    !isIdentityLoading && isClubFetched && !isClubLoading && !club &&
    (isClubAdmin || !hasClubAdminUids);

  const handleClubCreateClose = () => {
    queryClient.invalidateQueries([
      Resources.Clubs,
      identity?.[UserFields.Uid],
      'checkExists',
    ]);
  };

  return (
    <AppLocationContext>
      <RedirectBillingIfNoSubscription isEmployee={isCreateClub || isEmployee} />
      <Dialog sx={[formSx.formDialog, {}]} onClose={() => false} open={showAdminSwitch}>
        <AdminSwitchModal />
      </Dialog>

      <Dialog
        sx={[formSx.formDialogLarge, {}]}
        open={showClubCreateModal || isCreateClub}
      >
        {isClubAdmin ? (
          <ClubCreateAdmin onClose={handleClubCreateClose} />
        ) : (
          <ClubCreateUser onClose={handleClubCreateClose} />
        )}
      </Dialog>

      {isIdentityLoading ? (
        <LLLoaderComponent
          isLoading={true}
          sx={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      ) : (
        <Layout
          {...props}
          sx={[sx.root, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
          menu={COCMenu}
          appBar={COCAppBar}
          sidebar={(sideBarProps) => <COCSideBar {...(sideBarProps as SidebarProps)} />}
        >
          <input
            id="copyInput"
            style={{ top: 0, left: 0, right: '100%', position: 'fixed' }}
          />
          {props.children}
        </Layout>
      )}
    </AppLocationContext>
  );
};

export default AppLayout;
